import React from "react";
import InlineText from "./EditorialInline";
import { usePublicLanguage } from "../context/PublicLanguage";

export function SourceAttribution({ sources = [] }) {
  const { t, language } = usePublicLanguage();
  const names = [...new Map(sources.filter((source) => source.publisher?.trim()).map((source) =>
    [source.publisher.trim().toLowerCase(), source.publisher.trim()])).values()];
  if (!names.length) return null;
  return <section lang={language} aria-label={t("Sources")} className="mt-12 rounded-lg border p-6" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0c0d0e" }}>
    <p className="text-[#A7ABB0] text-sm"><span className="font-display font-bold text-white">{t(names.length === 1 ? "Source" : "Sources")}:</span> {names.join(", ")}</p>
  </section>;
}

function Block({ block, sources }) {
  switch (block.type) {
    case "h2":
      return <h2><InlineText text={block.text} sources={sources} /></h2>;
    case "h3":
      return <h3><InlineText text={block.text} sources={sources} /></h3>;
    case "paragraph":
      return <p><InlineText text={block.text} sources={sources} /></p>;
    case "list":
      return (
        <ul>
          {block.items.map((it, i) => (
            <li key={i}><InlineText text={it} sources={sources} /></li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <figure className="my-8 pl-6" style={{ borderLeft: "3px solid #FF8A1F" }}>
          <blockquote className="font-display italic text-white leading-relaxed" style={{ fontSize: "1.45rem" }}>
            “<InlineText text={block.text} />”
          </blockquote>
          {block.cite && (
            <figcaption className="td-eyebrow mt-3" style={{ color: "#71767c", fontSize: "0.6rem" }}>
              — <InlineText text={block.cite} />
            </figcaption>
          )}
        </figure>
      );
    case "image":
      return (
        <figure className="my-8 -mx-2 md:mx-0">
          <img src={block.url} alt={block.caption} loading="lazy" className="w-full rounded-md" style={{ background: "#111315" }} />
          {block.caption && (
            <figcaption className="mt-2.5 text-[#71767c] text-sm font-mono-td leading-snug px-1">{block.caption}</figcaption>
          )}
        </figure>
      );
    case "code":
      return (
        <div className="my-7 rounded-md overflow-hidden border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <div className="px-4 py-2 border-b flex items-center justify-between" style={{ background: "#0c0d0e", borderColor: "rgba(255,255,255,0.08)" }}>
            <span className="td-eyebrow" style={{ color: "#71767c", fontSize: "0.56rem" }}>
              {block.lang}
            </span>
          </div>
          <pre className="p-4 overflow-x-auto" style={{ background: "#0a0b0c" }}>
            <code className="font-mono-td text-[#E9E9E4]" style={{ fontSize: "0.85rem", lineHeight: 1.7 }}>
              {block.code}
            </code>
          </pre>
        </div>
      );
    case "table":
      return (
        <div className="my-7 overflow-x-auto rounded-md border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <table className="w-full text-left" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ background: "#111315" }}>
                {block.headers.map((h, i) => (
                  <th key={i} className="px-4 py-3 td-eyebrow" style={{ color: "#FF8A1F", fontSize: "0.58rem" }}>
                    <InlineText text={h} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="border-t" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 text-[#D5D5D0]">
                      <InlineText text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticleBody({ blocks = [], sources = [] }) {
  return (
    <div className="article-body">
      {blocks.map((b, i) => (
        <Block key={i} block={b} sources={sources} />
      ))}
    </div>
  );
}
